<div class="doc-content">
  <h1>Authentication</h1>

  <section class="doc-section">
    <h2>Security & Authentication</h2>
    <p>
      The connection is secured using 128‐bit encrypted transaction, using HTTPS. Idea Financial Universal Broker API provides token-based
      authentication therefore each request must contain authentication and token details.
    </p>
    <p>
      Please contact your Idea Financial Partner Representative for your authentication account information.
    </p>
  </section>

  <section class="doc-section">
    <h2>Token</h2>
    <p>
      In order to access any web service provided by Idea Financial Universal Broker API, the user needs to make a call to the OAuth2 service
      to get a token. This service will provide the token that is used to access any web services until the token is expired.
    </p>

    <h3>Endpoints</h3>
    <ul class="endpoints-list">
      <li><strong>Sandbox:</strong> https://oauth.ideafinancial.net/token</li>
      <li><strong>Production:</strong> https://oauth.ideafinancial.com/token</li>
    </ul>

    <h3>Description</h3>
    <ul>
      <li><strong>HTTP Method:</strong> POST</li>
      <li><strong>Headers:</strong> Content-Type: application/x-www-form-urlencoded</li>
    </ul>

    <h3>Payload</h3>
    <div class="code-block">
      <code>
        grant_type=client_credentials<br>
        client_id=[PROVIDED SEPARATELY]<br>
        client_secret=[PROVIDED SEPARATELY]
      </code>
    </div>

    <h3>Successful Response</h3>
    <p>Successful will return HTTP status code 200 and the following payload:</p>
    <ul>
      <li><strong>access_token</strong> [String, authentication token to be used in any call to Ryno Broker API]</li>
      <li><strong>token_type</strong> [String, token type (always will be "Bearer")]</li>
      <li><strong>expires_at</strong> [Integer, represents token validity lifetime in seconds]</li>
      <li><strong>refresh_token</strong> [String: token to be used when access_token lifetime passes]</li>
    </ul>

    <h3>Error Response</h3>
    <p>Error response will return status 400, error code and reason:</p>
    <ul>
      <li><strong>error</strong> [String, error code]</li>
      <li><strong>error_description</strong> [String, error description]</li>
    </ul>
  </section>

  <section class="doc-section">
    <h2>Examples</h2>
    <h3>Request</h3>
    <div class="code-block">
      <code>
        Content-Type: application/x-www-form-urlencoded<br><br>
        grant_type=client_credentials&client_id=[CLIENT_ID]&client_secret=[CLIENT_SECRET]
      </code>
    </div>

    <h3>Success Response</h3>
    <div class="code-block">
      <code>
        Status: 200<br><br>
        {{ '{' }}<br>
        &nbsp;&nbsp;"access_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCIsIm1lc3NhZ2UiOiJ0aGlzIGlzIG9ubHkgZXhhbXBsZSJ9.e30.9SJ7lsvw5Y5ExWLC1ZKibQOFZ-WgrxkFK09NKaxJ7rw",<br>
        &nbsp;&nbsp;"token_type": "Bearer",<br>
        &nbsp;&nbsp;"expires_at": 3600,<br>
        &nbsp;&nbsp;"refresh_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCIsIm1lc3NhZ2UiOiJleGFtcGxlIn0.e30.rGesVsImxcWSFevYsWHgLpbFat_fuE6wpm7mF6PZBsc"<br>
        {{ '}' }}
      </code>
    </div>

    <h3>Error Response</h3>
    <div class="code-block">
      <code>
        Status: 400<br><br>
        {{ '{' }}<br>
        &nbsp;&nbsp;"error": "invalid_grant",<br>
        &nbsp;&nbsp;"error_description": "authentication failure"<br>
        {{ '}' }}
      </code>
    </div>
  </section>

  <section class="doc-section">
    <h2>Including Your API Key in Requests</h2>
    <p>Include your API key in the HTTP Authorization header of all requests:</p>
    <div class="code-block">
      <code>Authorization: Bearer YOUR_API_TOKEN</code>
    </div>
    <p>Example using cURL:</p>
    <div class="code-block">
      <code>curl --location 'https://partner.api.ideafinancial.net/v1/applications/[application_id]' \<br>
        --header 'Authorization: ••••••'</code>
    </div>
  </section>

  <section class="doc-section">
    <h2>API Key Security</h2>
    <p>Keep your API keys secure:</p>
    <ul>
      <li>Never share your API keys in publicly accessible areas</li>
      <li>Don't commit API keys to source control</li>
      <li>Rotate keys periodically</li>
      <li>Use separate keys for development and production</li>
    </ul>
  </section>

  <section class="doc-section">
    <h2>Authentication Flow</h2>
    <div class="api-image-container">
      <img src="assets/api-docs/graphs/authentication_graph.png" alt="Authentication Flow Diagram" class="api-flow-image">
    </div>
  </section>
</div>
